import * as THREE from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'

import { getResourcePath } from '../API/storageAPI'

//export const SKYBOX_TEXTURE_NAME = 'Materials/SpecularSkybox6.jpg'
//export const SKYBOX_TEXTURE_NAME = 'Materials/2blights_0.jpg'
//export const SKYBOX_TEXTURE_NAME = 'Materials/black.jpg'
export const SKYBOX_TEXTURE_NAME = 'Materials/diamond5.hdr'

export const SKYBOX_DIFRACTION_TEXTURE_NAME = 'Materials/2blights_0.jpg'
//export const INNER_TEX_TEXTURE_NAME = 'Materials/CloudsRainbow_c.jpeg'
//export const INNER_TEX_TEXTURE_NAME = 'Materials/CloudsRainbow_c2-xv40_pano.jpg'
export const INNER_TEX_TEXTURE_NAME = 'Materials/2blights_0.jpg'
//export const INNER_TEX_TEXTURE_NAME = 'Materials/SpecularSkybox2.jpg'

// export const INNER_SPECULAR_D1_TEXTURE_NAME = 'Materials/2blights_0.jpg'
// export const INNER_SPECULAR_D2_TEXTURE_NAME = 'Materials/black.jpg'
// export const INNER_SPECULAR_D3_TEXTURE_NAME = 'Materials/black.jpg'

export const INNER_SPECULAR_D1_TEXTURE_NAME = 'Materials/diamond.hdr'
export const INNER_SPECULAR_D2_TEXTURE_NAME = 'Materials/diamond.hdr'
export const INNER_SPECULAR_D3_TEXTURE_NAME = 'Materials/diamond.hdr'

//export const INNER_TEX_TEXTURE_NAME = 'Materials/ColoredPano.jpg'
export const VERT_SHADER_NAME = 'Materials/DiamondShader2_2.vert'
export const FRAG_SHADER_NAME = 'Materials/DiamondShader_20230112_2.frag'

export type TextureConfig = {
  skybox_texture: string,
  inner_texture: string,
  inner_specular_d1: string,
  inner_specular_d2: string,
  inner_specular_d3: string
}

export function getDefaultTextures():TextureConfig {
  return {
    skybox_texture: SKYBOX_TEXTURE_NAME,
    inner_texture: INNER_TEX_TEXTURE_NAME,
    inner_specular_d1: INNER_SPECULAR_D1_TEXTURE_NAME,
    inner_specular_d2: INNER_SPECULAR_D2_TEXTURE_NAME,
    inner_specular_d3: INNER_SPECULAR_D3_TEXTURE_NAME
  }
}

export function addBaseLoadResourceTasks(
  loadResorceTasks: Promise<THREE.Texture | string>[],
  textures = getDefaultTextures()
): Promise<THREE.Texture | string>[] {
  const loadReflectionMap = loadTexture(textures.skybox_texture)
  const loadInnerReflectionMap = loadTexture(textures.inner_texture)
  const loadInnerSpecularD1 = loadTexture(textures.inner_specular_d1)
  const loadInnerSpecularD2 = loadTexture(textures.inner_specular_d2)
  const loadInnerSpecularD3 = loadTexture(textures.inner_specular_d3)

  loadResorceTasks.push(loadReflectionMap, loadInnerReflectionMap, loadInnerSpecularD1, loadInnerSpecularD2, loadInnerSpecularD3)
  
  return loadResorceTasks
}

export function getBaseUniforms(resources: (THREE.Texture | string)[], jewelry: THREE.Mesh) {
  const color = getColorFromMesh(jewelry)
  const loadInnerSpecularD3 = resources.pop()
  const loadInnerSpecularD2 = resources.pop()
  const loadInnerSpecularD1 = resources.pop()
  const loadInnerReflectionMap = resources.pop()
  const loadReflectionMap = resources.pop()

  const uniforms = {
    _RefractionIndex: { value: 2.42 },
    _OutReflectionMap: { value: loadReflectionMap },
    _InnerReflectionMap: { value: loadInnerReflectionMap },
    _CenterDrift: { value: 0 },
    _NormalMapDrift: { value: 1 },
    _Reflection: { value: 1.0 },
    _Refraction: { value: 1 },
    _Color: { value: color, },
    _Opacity: { value: 1.0 },
    _NormalRot: { value: 3.14159 },
    _InnerSpecular: { value: 0.7 },
    _OutSpecular: { value: 1.0 },
    _SpecularMap: { value: loadReflectionMap },
    _ReflectionMap: { value: loadInnerReflectionMap },
    _NormalOutZrot: { value: 1.0 },
    _NormalOutXrot: { value: 3.0 },
    _NormalOutYrot: { value: 1.0 },
    _NormalZrot: { value: 0.0 },
    _NormalXrot: { value: 3.0 },
    _NormalYrot: { value: 0.0 },
    _NormalOutReflect: { value: 1.0 },
    _InNegX: { value: -1.0 },
    _InNegY: { value: 1.0 },
    _InNegZ: { value: 1.0 },
    _yNormalScale: { value: 1.0 },
    _OutNegX: { value: -1.0 },
    _OutNegZ: { value: 1.0 },
    _SpecularMapD1: { value: loadInnerSpecularD1 },
    _SpecularMapD2: { value: loadInnerSpecularD2 },
    _SpecularMapD3: { value: loadInnerSpecularD3 }
  }

  return uniforms
}

const textureCache = new Map<string, THREE.Texture>()
export async function loadTexture(name: string): Promise<THREE.Texture> {
  const textureFormat = name.split('.').pop()
  const textureLoader = textureFormat === 'hdr' ? new RGBELoader() : new THREE.TextureLoader()
  
  let texture = textureCache.get(name)
  if (!texture) {
    texture = await textureLoader.loadAsync(getResourcePath(name))

    texture.generateMipmaps = false

    texture.repeat = new THREE.Vector2(5, 5);
  
    texture.wrapS = THREE.MirroredRepeatWrapping
    texture.wrapT = THREE.MirroredRepeatWrapping
  
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearFilter;
    
    textureCache.set(name, texture)
  }

  return texture
}

const shaderCache = new Map<string, string>()
export async function loadShader(name: string): Promise<string> {
  let shader = shaderCache.get(name)
  if (!shader) {
    shader = await (await fetch(getResourcePath(name))).text()
    shaderCache.set(name, shader)
  }
  return shader
}

function getColorFromMesh(mesh: THREE.Mesh) {
  if (mesh instanceof THREE.Mesh && mesh.material instanceof THREE.MeshStandardMaterial) {
    const color = mesh.material.color
    return new THREE.Vector4(color.r, color.g, color.b, 1.0)
  }
  return new THREE.Vector4(1, 1, 1, 1)
}
